import React, { useRef, useState } from 'react';

import { ScanLine, X } from 'lucide-react';

import { fieldFrame } from './fieldFrame';
import { cn } from './utils';


/**
 * Editable multi-value identifier input — the DESIGN.md `mono-input` token and
 * the .pen UI-Kit frame "OE / part numbers (mono · multi)" (`lxjnt`): a
 * surface-1 / hairline-strong / md-radius control frame holding mono chips, a
 * free-text entry, and an optional scan affordance.
 *
 * CONTROL GEOMETRY COMES FROM DESIGN.md, NOT THE FRAME. `.ai/DESIGN.md`
 * `mono-input` specifies 40px height / 0 12px padding — the same 40/12 every
 * sibling control uses (`Input`, `Select`, `NumberStepper`) — while frame
 * `lxjnt` draws 44/10. DESIGN.md wins: it is the cross-component contract, and a
 * 44px control would sit 4px below the 40px `AutocompleteField` it shares a
 * `flex gap-3` row with in `pages/Inventory/partFormSections.jsx`. (Same ruling
 * as `NumberStepper`, whose frame is likewise 2px stale against DESIGN.md.)
 *
 * Everything the frame is authoritative for is carried over: 7px gap; chips are
 * `rounded-sm` surface-2 with a hairline stroke, 4/8 padding, mono 11.5px, and
 * an `x` remove glyph; the scan icon is primary.
 *
 * Values are an ARRAY of strings. `Enter`, `,` and blur commit the typed entry;
 * `Backspace` on an empty entry removes the last chip. Duplicates and blanks are
 * dropped, so the emitted array is always the clean identifier list a payload
 * wants. This is the editable counterpart to the read-only chips renderer in
 * `pages/Inventory/photoIntake/gates.jsx` (which displays AI-suggested numbers
 * and must stay read-only).
 *
 * `onScan` is optional; the scan button only renders when it is supplied (the
 * frame notes it as a phone affordance).
 */
const MonoInput = React.forwardRef((props, ref) => {
  const {
    values = [],
    onChange,
    onScan,
    onBlur,
    placeholder = 'Add…',
    invalid = false,
    disabled = false,
    id,
    className,
    ...restProps
  } = props;

  const [draft, setDraft] = useState('');
  const inputRef = useRef(null);

  const setRef = (node) => {
    inputRef.current = node;
    if (typeof ref === 'function') {
      ref(node);
    }
    else if (ref) {
      ref.current = node;
    }
  };

  const commit = (text) => {
    const entry = text.trim();
    setDraft('');
    if (!entry || values.includes(entry)) {
      return;
    }
    onChange?.([...values, entry]);
  };

  const removeAt = (index) => {
    onChange?.(values.filter((_, position) => position !== index));
    inputRef.current?.focus();
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ',') {
      // Enter must not reach a surrounding form's implicit submit while the user
      // is still entering identifiers (same rule as the combobox primitives).
      event.preventDefault();
      commit(draft);
      return;
    }
    if (event.key === 'Backspace' && draft === '' && values.length) {
      event.preventDefault();
      onChange?.(values.slice(0, -1));
    }
  };

  return (
    <div
      className={cn(
        fieldFrame({ focus: 'within', invalid }),
        // py-1 (not py-1.5) so the natural height — 28px input + 8px padding +
        // 2px border — stays UNDER 40 and `min-h-10` pins the single-row case to
        // exactly 40, matching Input/Select. It still grows when chips wrap.
        'flex min-h-10 w-full flex-wrap items-center gap-[7px] px-3 py-1',
        disabled && 'cursor-not-allowed opacity-45',
        className,
      )}
    >
      {/* Keyed on index+value, not value alone: new entries are deduped on
          commit, but `values` loaded from the API is not — `oe_numbers` has no
          server-side uniqueness — so two identical stored numbers would collide
          on a value-only key. */}
      {values.map((value, index) => (
        <span
          key={`${index}-${value}`}
          className={cn(
            'inline-flex items-center gap-[5px] rounded-sm border border-hairline',
            'bg-surface-2 px-2 py-1 font-mono text-[11.5px] text-ink',
          )}
        >
          {value}

          <button
            type="button"
            disabled={disabled}
            aria-label={`Remove ${value}`}
            className="text-ink-subtle outline-none transition-colors hover:text-ink focus-visible:text-ink"
            onClick={() => removeAt(index)}
          >
            <X className="size-[11px]" />
          </button>
        </span>
      ))}

      {/* restProps FIRST: the form layer forwards RHF's `onBlur`/`name`, and a
          trailing spread would clobber the commit handlers below (the same trap
          the Autocomplete primitive documents). RHF's own onBlur is chained. */}
      <input
        {...restProps}
        ref={setRef}
        id={id}
        type="text"
        value={draft}
        disabled={disabled}
        className={cn(
          'h-7 min-w-24 flex-1 bg-transparent font-mono text-[12.5px] text-ink',
          'placeholder:font-sans placeholder:text-ink-subtle outline-none',
          'disabled:cursor-not-allowed',
        )}
        onKeyDown={handleKeyDown}
        placeholder={values.length ? '' : placeholder}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={(event) => {
          commit(draft);
          onBlur?.(event);
        }}
      />

      {onScan
        ? (
          <button
            type="button"
            disabled={disabled}
            aria-label="Scan a part number"
            className="flex shrink-0 items-center text-primary outline-none disabled:cursor-not-allowed"
            onClick={onScan}
          >
            <ScanLine className="size-4" />
          </button>
        )
        : null}
    </div>
  );
});

MonoInput.displayName = 'MonoInput';

export { MonoInput };
export default MonoInput;
